import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided. Please use the link from your email.');
      return;
    }
    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const res = await fetch(`${API}/api/auth/verify-email?token=${token}`, { method: 'POST' });
      const data = await res.json();

      if (data.already_verified) {
        // Already verified — show success with login prompt
        setStatus('already_verified');
        setMessage(data.message || 'Your email is already verified!');
        toast.success('Already verified — just log in!');
      } else if (res.ok) {
        setStatus('success');
        setMessage(data.message || 'Your email has been verified successfully!');
        setPlanInfo({ id: data.plan_id, name: data.plan_name });
        toast.success('Email verified! You can now log in.');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed. The link may have expired.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again or contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">

        {status === 'verifying' && (
          <>
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-stone-800 mb-2">Verifying Your Email</h1>
            <p className="text-stone-600">Please wait while we verify your email address...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-green-800 mb-2">You're All Set!</h1>
            <p className="text-stone-600 mb-4">Your email has been verified and your account is active.</p>
            {planInfo?.name && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">Your <strong>{planInfo.name}</strong> plan is active. Start by creating your first apiary!</p>
              </div>
            )}
            <Button onClick={() => navigate('/login')} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="goto-login-btn">
              <LogIn className="w-4 h-4 mr-2" />
              Sign In & Get Started
            </Button>
          </>
        )}

        {status === 'already_verified' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-green-800 mb-2">Already Verified!</h1>
            <p className="text-stone-600 mb-6">Your account is already active. Go ahead and log in to get started.</p>
            <Button onClick={() => navigate('/login')} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="goto-login-btn">
              <LogIn className="w-4 h-4 mr-2" />
              Go to Login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-red-800 mb-2">Verification Failed</h1>
            <p className="text-stone-600 mb-6">{message}</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                Already verified? Try logging in directly.
              </p>
            </div>
            <div className="space-y-3">
              <Button onClick={() => navigate('/login')} className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                <LogIn className="w-4 h-4 mr-2" />
                Go to Login
              </Button>
              <Link to="/trial-signup">
                <Button variant="ghost" className="w-full text-stone-500 mt-2">
                  Sign Up Again
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;
